import { CustomDatePickerInput } from "components/DatePicker/CustomDatePickerInput";
import { FieldInput } from "components/FieldInput";
import DatePicker from "react-datepicker";
import { enUS, pl } from "date-fns/locale";
import { Field } from "react-final-form";

import { PasswordInput } from "components/FieldInput/PasswordInput/PasswordInput";
import { useTranslationContext } from "context/translation";
import { LocalizationContext } from "context/localization";
import { isValidDate } from "helpers/date";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useContext, useMemo } from "react";
import {
  PasswordRequirement,
  useValidatePasswordLazyQuery,
} from "services/graphql/generated";
import st from "./RegisterFields.module.scss";

export const CreateAccountForm = () => {
  const path = usePathname();
  const {
    iAlreadyHaveAnAccount,
    createYourAccount,
    firstName,
    firstNamePlaceholder,
    lastName,
    lastNamePlaceholder,
    emailAddress,
    emailAddressPlaceholder,
    createAPassword,
    createPasswordPlaceholder,
    dateOfBirth,
    ageDisclaimerSubtext,
    linkConfirmationSubtext,
  } = useTranslationContext();
  const [validatePassword] = useValidatePasswordLazyQuery();
  const { localization } = useContext(LocalizationContext);

  const handlePasswordChange = async (
    password: string,
  ): Promise<PasswordRequirement[]> => {
    try {
      const { data, error } = await validatePassword({
        variables: { password },
      });
      if (error) throw error;
      return data?.validatePassword || [];
    } catch {
      return [];
    }
  };

  const maxDate = useMemo(() => {
    const minAgeDate = new Date();
    minAgeDate.setFullYear(new Date().getFullYear() - 18);
    return minAgeDate;
  }, []);

  const importedCalendarLocales = [pl, enUS];
  const currentLocale =
    importedCalendarLocales.find((l) => l.code === localization?.locale) ||
    enUS;

  return (
    <div>
      <div className={st.createAccountContainer}>
        <div className={st.formContainer}>
          <div className={st.createAccountTitle}>{createYourAccount}</div>
          <Link
            href={`/sign-in?return_url=${encodeURIComponent(path)}`}
            className={st.toLoginLink}
          >
            {iAlreadyHaveAnAccount}
          </Link>
          <div className={st.createAccountFormContainer}>
            <div>
              <div className={st.nameRow}>
                <FieldInput
                  name="firstName"
                  label={`${firstName || ""}*`}
                  placeholder={firstNamePlaceholder || ""}
                  inputClassName={st.nameInput}
                />
                <FieldInput
                  name="lastName"
                  label={`${lastName || ""}*`}
                  placeholder={lastNamePlaceholder || ""}
                  inputClassName={st.nameInput}
                />
              </div>
              <div className={st.formRow}>
                <FieldInput
                  name="email"
                  label={`${emailAddress || ""}*`}
                  type="email"
                  placeholder={emailAddressPlaceholder || ""}
                />
              </div>
              <p className={st.subtext}>{linkConfirmationSubtext}</p>
              <div className={st.formRow}>
                <PasswordInput
                  handlePasswordChange={handlePasswordChange}
                  name="password"
                  label={`${createAPassword || ""}*`}
                  type="password"
                  placeholder={createPasswordPlaceholder || ""}
                  defaultValue=""
                />
              </div>

              <div className={st.formRow}>
                <Field
                  name="birthday"
                  render={({ input: { value, onChange }, meta }) => (
                    <>
                      <DatePicker
                        className="w-full"
                        selected={
                          value && isValidDate(value) ? new Date(value) : null
                        }
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        showPreviousMonths={false}
                        onChange={onChange}
                        maxDate={maxDate}
                        customInput={
                          <CustomDatePickerInput
                            inputName="birthday"
                            label={dateOfBirth || ""}
                            localeCode={currentLocale.code}
                          />
                        }
                        excludeScrollbar
                        locale={currentLocale}
                      />
                      {!meta.touched && !meta.error && (
                        <p className={st.subtext}>{ageDisclaimerSubtext}</p>
                      )}
                    </>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
